import React, { FC, useCallback, useMemo, useEffect, useState } from 'react';
import { Radio, Input, InputNumber } from 'antd';
import { ActivityNotifyType } from '@/api/activityMng';
import {
  createTagDetailLink,
  createArtworkDetailLink,
  createWebviewLink,
  getValueFromLink,
  UniversalLinkPage,
  createBrowserLink,
  createContestDetailLink,
  createNewcomerContestLink,
} from '@/utils/universalLinks';

interface LinkFormValue {
  notifyType: ActivityNotifyType;
  link: string;
}

interface LinkSelectFormProps {
  disabled?: boolean;
  value?: Partial<LinkFormValue>;
  onChange?: (value: LinkFormValue) => void;
  placeholder?: string;
}

interface ComponentProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const LinkTextArea: FC<ComponentProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => (
  <Input.TextArea
    maxLength={500}
    rows={4}
    disabled={disabled}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange?.(e.target.value)}
  />
);

const LinkInput: FC<ComponentProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => (
  <Input
    disabled={disabled}
    placeholder={placeholder}
    style={{ width: '100%' }}
    value={value}
    onChange={(e) => onChange?.(e.target.value)}
  />
);

const LinkInputNumber: FC<ComponentProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => (
  <Input
    disabled={disabled}
    placeholder={placeholder}
    style={{ width: '100%' }}
    value={value}
    onChange={(e) => onChange?.(e.target.value)}
  />
);

const LINK_TYPE_CONFIG = [
  {
    value: ActivityNotifyType.LINK,
    label: '页面链接',
    component: LinkTextArea,
    placeholder: '请输入页面网址(最大500字符)',
    pageType: UniversalLinkPage.webview,
  },
  {
    value: ActivityNotifyType.BROWSER,
    label: '外部浏览器链接',
    component: LinkTextArea,
    placeholder: '请输入页面网址(最大500字符)',
    pageType: UniversalLinkPage.browser,
  },
  {
    value: ActivityNotifyType.TAG,
    label: '话题详情页',
    component: LinkInput,
    placeholder: '请输入话题名称',
    pageType: UniversalLinkPage.tagDetail,
  },
  {
    value: ActivityNotifyType.ARTWORK,
    label: '作品详情页',
    component: LinkInputNumber,
    placeholder: '请输入作品ID',
    pageType: UniversalLinkPage.artworkDetail,
  },
  {
    value: ActivityNotifyType.CONTEST,
    label: '比赛详情页',
    component: LinkInputNumber,
    placeholder: '请输入比赛ID',
    pageType: UniversalLinkPage.contestDetail,
  },
  {
    value: ActivityNotifyType.NEWCOMER_CONTEST,
    label: '新人王链接跳转',
    component: LinkInputNumber,
    placeholder: '请输入新人王ID',
    pageType: UniversalLinkPage.rookieKingDetail,
  },
] as const;

const LinkSelectForm: FC<LinkSelectFormProps> = ({
  disabled = false,
  value = { notifyType: ActivityNotifyType.LINK, link: '' },
  onChange,
  placeholder,
}) => {
  const currentConfig = useMemo(() => {
    return (
      LINK_TYPE_CONFIG.find((config) => config.value === value.notifyType) ||
      LINK_TYPE_CONFIG[0]
    );
  }, [value.notifyType]);

  const [rawValue, setRawValue] = useState('');

  // 根据 notifyType 与 link 解码 rawValue
  const decodeLink = useCallback(
    (notifyType: ActivityNotifyType, link: string): string => {
      const config = LINK_TYPE_CONFIG.find((c) => c.value === notifyType);
      if (!config) return '';

      return getValueFromLink(link, config.pageType);
    },
    []
  );

  // 根据 notifyType 与 rawValue 编码为通用链接
  const encodeLink = useCallback(
    (notifyType: ActivityNotifyType, raw: string): string => {
      switch (notifyType) {
        case ActivityNotifyType.TAG:
          return createTagDetailLink(raw);
        case ActivityNotifyType.ARTWORK:
          return createArtworkDetailLink(raw);
        case ActivityNotifyType.LINK:
          return createWebviewLink(raw);
        case ActivityNotifyType.BROWSER:
          return createBrowserLink(raw);
        case ActivityNotifyType.CONTEST:
          return createContestDetailLink(raw);
        case ActivityNotifyType.NEWCOMER_CONTEST:
          return createNewcomerContestLink(raw);
        default:
          return raw;
      }
    },
    []
  );

  // 当 value.notifyType 或 value.link 改变时，重新计算 rawValue
  useEffect(() => {
    const decoded = value.link
      ? decodeLink(value.notifyType ?? ActivityNotifyType.LINK, value.link)
      : '';
    setRawValue(decoded);
  }, [value.notifyType, value.link, decodeLink]);

  const handleTypeChange = useCallback(
    (type: ActivityNotifyType) => {
      // 切换类型时重置 rawValue
      setRawValue('');
      onChange?.({
        notifyType: type,
        link: encodeLink(type, ''),
      });
    },
    [onChange, encodeLink]
  );

  const handleRawValueChange = useCallback(
    (raw: string) => {
      setRawValue(raw);
      onChange?.({
        notifyType: value.notifyType ?? ActivityNotifyType.LINK,
        link: encodeLink(value.notifyType ?? ActivityNotifyType.LINK, raw),
      });
    },
    [onChange, value.notifyType, encodeLink]
  );

  const InputComponent = currentConfig.component;

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Radio.Group
          value={value.notifyType}
          onChange={(e) => handleTypeChange(e.target.value)}
          disabled={disabled}
        >
          {LINK_TYPE_CONFIG.map((config) => (
            <Radio key={config.value} value={config.value}>
              {config.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>
      <InputComponent
        value={rawValue}
        onChange={handleRawValueChange}
        disabled={disabled}
        placeholder={placeholder ?? currentConfig.placeholder}
      />
    </div>
  );
};

export default LinkSelectForm;
